import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ShieldCheck, User, Globe, KeyRound } from 'lucide-react';
import { agenciesAPI } from '../utils/api';

const AgencyLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState(1); // 1: Login, 2: OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agencyId, setAgencyId] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;
    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
    if (element.value !== '' && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await agenciesAPI.login(formData.email, formData.password);
      if (response.data.otp_required) {
        setAgencyId(response.data.agency_id);
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const otpString = otp.join('');
      const response = await agenciesAPI.verifyOtp(agencyId, otpString);
      if (response.data.success) {
        const { token, password_reset_required } = response.data;
        sessionStorage.setItem('agencyToken', token.accessToken);
        if (password_reset_required) {
          sessionStorage.setItem('forcePasswordReset', 'true');
        }
        navigate('/agency/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 font-['Outfit'] overflow-hidden relative">
      {/* Dynamic Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-pink-600/20 blur-[120px] rounded-full animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/20 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      
      <div className="w-full max-w-[450px] relative z-10">
        {/* Glass Container */}
        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[40px] p-8 md:p-12 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600"></div>

          {/* Logo & Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-tr from-pink-600 to-indigo-600 p-[2px] mb-6 group transition-transform hover:scale-110 duration-500">
               <div className="w-full h-full bg-[#0f172a] rounded-[22px] flex items-center justify-center">
                  <ShieldCheck size={40} className="text-white group-hover:rotate-12 transition-transform" />
               </div>
            </div>
            <h1 className="text-4xl font-black text-white tracking-tighter mb-2">
              XOXO <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-indigo-500">AGENCY</span>
            </h1>
            <p className="text-slate-500 font-medium">Empowering Creators, Managing Success.</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-500 text-sm font-bold text-center animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Agency Identifier</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-pink-500 transition-colors">
                    <User size={20} />
                  </div>
                  <input
                    type="text"
                    name="email"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-600 outline-none focus:border-pink-500/50 focus:bg-white/10 transition-all"
                    placeholder="Agency ID or Email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-500 transition-colors">
                    <Lock size={20} />
                  </div>
                  <input
                    type="password"
                    name="password"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/50 focus:bg-white/10 transition-all"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full group relative py-4 bg-gradient-to-r from-pink-600 to-indigo-600 rounded-2xl font-bold text-white shadow-[0_0_20px_rgba(219,39,119,0.3)] hover:shadow-[0_0_30px_rgba(219,39,119,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              >
                <div className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? 'Authenticating...' : 'Proceed to OTP'}
                  {!loading && <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />}
                </div>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="text-center">
                 <div className="w-16 h-16 bg-pink-500/10 rounded-2xl flex items-center justify-center text-pink-500 mx-auto mb-4">
                    <KeyRound size={32} />
                 </div>
                 <h2 className="text-xl font-bold text-white">Verification Code</h2>
                 <p className="text-slate-500 text-sm mt-2">Enter the 6-digit code sent to your App</p>
              </div>

              <div className="flex justify-between gap-2">
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    className="w-12 h-14 bg-white/5 border border-white/10 rounded-xl text-center text-2xl font-black text-white outline-none focus:border-pink-500 focus:bg-white/10 transition-all"
                    value={data}
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={loading || otp.join('').length < 6}
                  className="w-full py-4 bg-gradient-to-r from-pink-600 to-indigo-600 rounded-2xl font-bold text-white shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Login to Dashboard'}
                </button>
                
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full py-2 text-slate-500 text-sm font-bold hover:text-white transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}

          <div className="mt-10 pt-8 border-t border-white/5 flex items-center justify-between">
             <div className="flex items-center gap-2 text-slate-600 hover:text-slate-400 transition-colors cursor-pointer">
                <Globe size={14} />
                <span className="text-xs font-bold uppercase tracking-widest">English</span>
             </div>
             <p className="text-[10px] text-slate-700 font-bold uppercase tracking-[0.2em]">© 2026 XOXO GLOBAL</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyLogin;
